import { inject, injectable } from 'inversify'
import { SERVICE_TYPES } from '@/core/container/types'
import type { Resolvable } from '@/services/resolvers/types'
import type RouterService from '@/services/route/routerService'
import type { RouterPermissionInterface, Translatable } from '@/services/types'
import type { TmRoutes } from '@/services/route/routerTypes'
import { composeMessageRouteName } from '@/routes/user/compose'
import { UserSettingsRoutes } from '@/routes/user/settings'
import type {
  SettingsBillingChildRoutes,
  SettingSmsVoiceChildSections,
  SettingsMyAccountChildRoutes,
  SettingsWorkspaceChildRoutes,
} from '@/services/domain/settings/types'
import type { TmMenuItemBadge } from '@/components/shared/types'

export type PageContentSidebarLinkItemKeys =
  | 'smsVoice'
  | 'email'
  | 'chatWidget'
  | 'whatsApp'
  | 'facebook'
  | 'instagram'
  | 'messenger'
  | 'contacts'
  | 'api'
  | 'mcp'
  | 'integrations'
  | 'emailToSms'
  | 'automationRules'
  | 'tickets'
  | 'tasks'
  | 'deals'
  | 'pointAi'
  | 'workspace'
  | 'billing'
  | 'account'

interface PageContentSidebarLinkChildRoute<T extends string> {
  name: T
  title: string
  to: TmRoutes
  badge?: TmMenuItemBadge
}
export type PageContentSidebarLink = {
  itemType: 'link'
  itemKey: PageContentSidebarLinkItemKeys
  title: string
  route: TmRoutes
  badge?: 'new' | 'beta'
  overrideActive?: boolean
  icon: string
  childrenRoutesList?: PageContentSidebarLinkChildRoute<string>[]
  pageRoutesList?: Record<string, TmRoutes>
}

export type PageContentSidebarGroupItemKeys = 'channels' | 'features' | 'administration'

type PageContentSidebarGroup = {
  itemType: 'group'
  itemKey: PageContentSidebarGroupItemKeys
  title: string
  links: PageContentSidebarLink[]
}

@injectable()
export default class SettingsSectionRedirectResolver implements Resolvable {
  constructor(
    @inject(SERVICE_TYPES.RouterPermissionsService)
    private readonly routerPermissionsService: RouterPermissionInterface,
    @inject(SERVICE_TYPES.RouterService) private readonly routerService: RouterService,
    @inject(SERVICE_TYPES.TranslateService) private readonly translateService: Translatable,
  ) {}

  public getSmsVoiceChildRoutesRecord(): Record<SettingSmsVoiceChildSections, TmRoutes> {
    return {
      numbers: { name: UserSettingsRoutes.SmsVoiceNumbers },
      senderIds: { name: UserSettingsRoutes.SmsVoiceSenderIds },
      byoc: { name: UserSettingsRoutes.SmsVoiceByocIndex },
      senderSettings: { name: UserSettingsRoutes.SmsVoiceSenderSettings },
      tenDlc: { name: UserSettingsRoutes.SmsVoiceTenDlcIndex },
      tollFree: { name: UserSettingsRoutes.SmsVoiceTollFree },
    }
  }

  public getWorkspaceChildRoutesRecord(): Record<SettingsWorkspaceChildRoutes, TmRoutes> {
    return {
      workspaceUsers: { name: UserSettingsRoutes.WorkspaceUsers },
      workspaceSubaccounts: { name: UserSettingsRoutes.WorkspaceSubaccounts },
      workspaceSso: { name: UserSettingsRoutes.WorkspaceSso },
      workspaceDataRetention: { name: UserSettingsRoutes.WorkspaceDataRetention },
    }
  }

  public getWorkspaceChildRoutes(): PageContentSidebarLinkChildRoute<SettingsWorkspaceChildRoutes>[] {
    const routes = [
      {
        name: 'workspaceUsers',
        title: this.translateService.t('pageTitle.user.accounts.users'),
      },
      {
        name: 'workspaceSubaccounts',
        title: this.translateService.t('pageTitle.user.accounts.subAccounts.title'),
      },
      {
        name: 'workspaceSso',
        title: this.translateService.t('pageTitle.user.accounts.sso'),
      },
      {
        name: 'workspaceDataRetention',
        title: this.translateService.t('pageTitle.user.accounts.dataRetention'),
      },
    ] as const

    const routesRecord = this.getWorkspaceChildRoutesRecord()

    return routes.map((t) => {
      return {
        ...t,
        to: routesRecord[t.name],
      }
    })
  }

  public getBillingChildRoutesRecord(): Record<SettingsBillingChildRoutes, TmRoutes> {
    return {
      overview: { name: UserSettingsRoutes.BillingOverview },
      plans: { name: UserSettingsRoutes.BillingPlans },
      invoices: { name: UserSettingsRoutes.BillingInvoices },
      statements: { name: UserSettingsRoutes.BillingStatements },
    }
  }

  public getBillingChildRoutes(): PageContentSidebarLinkChildRoute<SettingsBillingChildRoutes>[] {
    const routes = [
      {
        name: 'overview',
        title: this.translateService.t('pageTitle.user.settings.billingOverview'),
      },
      {
        name: 'plans',
        title: this.translateService.t('pageTitle.user.settings.billingPlans'),
      },
      {
        name: 'invoices',
        title: this.translateService.t('pageTitle.user.settings.billingInvoices'),
      },
      {
        name: 'statements',
        title: this.translateService.t('pageTitle.user.settings.billingStatements'),
      },
    ] as const

    const routesRecord = this.getBillingChildRoutesRecord()

    return routes.map((t) => {
      return {
        ...t,
        to: routesRecord[t.name],
      }
    })
  }

  public getAccountChildRoutesRecord(): Record<SettingsMyAccountChildRoutes, TmRoutes> {
    return {
      details: { name: UserSettingsRoutes.AccountDetails },
      security: { name: UserSettingsRoutes.AccountSecurity },
      notifications: { name: UserSettingsRoutes.AccountNotifications },
    }
  }

  public getAccountChildRoutes(): PageContentSidebarLinkChildRoute<SettingsMyAccountChildRoutes>[] {
    const routes = [
      {
        name: 'details',
        title: this.translateService.t('pageTitle.user.settings.accountDetails'),
      },
      {
        name: 'security',
        title: this.translateService.t('pageTitle.user.settings.accountSecurity'),
      },
      {
        name: 'notifications',
        title: this.translateService.t('pageTitle.user.settings.accountNotifications'),
      },
    ] as const

    const routesRecord = this.getAccountChildRoutesRecord()

    return routes.map((t) => {
      return {
        ...t,
        to: routesRecord[t.name],
      }
    })
  }

  public getRoutesGroups() {
    const channelsLinks: PageContentSidebarLink[] = [
      {
        itemType: 'link',
        itemKey: 'smsVoice',
        title: this.translateService.t('pageTitle.user.settings.smsVoice'),
        route: { name: UserSettingsRoutes.SmsVoice },
        icon: 'chat',
        pageRoutesList: this.getSmsVoiceChildRoutesRecord(),
      },
      {
        itemType: 'link',
        itemKey: 'email',
        title: this.translateService.t('pageTitle.user.settings.email'),
        route: { name: UserSettingsRoutes.EmailIndex },
        icon: 'mail',
      },
      {
        itemType: 'link',
        itemKey: 'chatWidget',
        title: this.translateService.t('pageTitle.user.settings.liveChat'),
        route: { name: UserSettingsRoutes.LiveChatIndex },
        icon: 'tmi-testimonial-comment',
      },
      {
        itemType: 'link',
        itemKey: 'whatsApp',
        title: this.translateService.t('pageTitle.user.settings.whatsApp'),
        route: { name: UserSettingsRoutes.WhatsAppIndex },
        icon: 'tmi-whatsapp-phone',
      },
      {
        itemType: 'link',
        itemKey: 'facebook',
        title: this.translateService.t('pageTitle.user.settings.facebook'),
        route: { name: UserSettingsRoutes.FacebookIndex },
        icon: 'tmi-facebook',
      },
      {
        itemType: 'link',
        itemKey: 'instagram',
        title: this.translateService.t('pageTitle.user.settings.instagram'),
        route: { name: UserSettingsRoutes.InstagramIndex },
        icon: 'tmi-instagram',
      },
    ]

    const apiTitle = this.translateService.t('pageTitle.user.settings.api')

    const currentRoute = this.routerService.getCurrentRoute()
    const isCurrentRouteOneOf = (routeNames: UserSettingsRoutes[]) => routeNames.some((t) => t === currentRoute.name)

    const mcpOverrideActive = isCurrentRouteOneOf([UserSettingsRoutes.IntegrationsMcpDetails]) ? true : undefined
    const integrationsOverrideActive = isCurrentRouteOneOf([
      UserSettingsRoutes.IntegrationDetails,
      UserSettingsRoutes.IntegrationSyncDetails,
      UserSettingsRoutes.IntegrationSetup,
    ])
      ? true
      : undefined

    const featuresLinks: PageContentSidebarLink[] = [
      {
        itemType: 'link',
        itemKey: 'mcp',
        title: this.translateService.t('pageTitle.user.settings.integrationsMcp'),
        route: { name: UserSettingsRoutes.IntegrationsMcp },
        icon: 'tmi-mcp',
        badge: 'new',
        overrideActive: mcpOverrideActive,
      },
      {
        itemType: 'link',
        itemKey: 'integrations',
        title: this.translateService.t('pageTitle.user.settings.integrations'),
        route: { name: UserSettingsRoutes.Integrations },
        icon: 'tmi-connection',
        badge: 'new',
        overrideActive: integrationsOverrideActive,
      },
      {
        itemType: 'link',
        itemKey: 'messenger',
        title: this.translateService.t('pageTitle.user.settings.messenger'),
        route: { name: UserSettingsRoutes.Messenger },
        icon: 'chat',
      },
      {
        itemType: 'link',
        itemKey: 'contacts',
        title: this.translateService.t('pageTitle.user.settings.contacts'),
        route: { name: UserSettingsRoutes.ContactsIndex },
        icon: 'tmi-people',
      },
      {
        itemType: 'link',
        itemKey: 'api',
        title: apiTitle,
        route: { name: UserSettingsRoutes.ApiIndex },
        icon: 'tmi-brackets',
      },
      {
        itemType: 'link',
        itemKey: 'emailToSms',
        title: this.translateService.t('pageTitle.user.settings.emailToSms'),
        route: { name: UserSettingsRoutes.EmailToSmsIndex },
        icon: 'tmi-chat-email',
      },
      {
        itemType: 'link',
        itemKey: 'automationRules',
        title: this.translateService.t('pageTitle.user.settings.autoResponders'),
        route: { name: UserSettingsRoutes.AutoRespondersIndex },
        icon: 'tmi-reply-inbox',
      },
      {
        itemType: 'link',
        itemKey: 'tickets',
        title: this.translateService.t('pageTitle.user.settings.tickets'),
        route: { name: UserSettingsRoutes.TicketsIndex },
        icon: 'inbox',
      },
      {
        itemType: 'link',
        itemKey: 'tasks',
        title: this.translateService.t('pageTitle.user.settings.tasks'),
        route: { name: UserSettingsRoutes.Tasks },
        icon: 'tmi-tasks',
      },
      {
        itemType: 'link',
        itemKey: 'deals',
        title: this.translateService.t('pageTitle.user.settings.deals'),
        route: { name: UserSettingsRoutes.Deals },
        icon: 'tmi-deals-monetization-on2',
      },
      {
        itemType: 'link',
        itemKey: 'pointAi',
        title: this.translateService.t('pageTitle.user.settings.pointAi'),
        route: { name: UserSettingsRoutes.PointAiIndex },
        icon: 'tmi-stars-filled',
      },
    ]

    const workspaceRoute = this.getWorkspaceChildRoutes().find((t) =>
      this.routerPermissionsService.isAllowAccessToRoute(t.to),
    )

    const administrationLinks: PageContentSidebarLink[] = []

    if (workspaceRoute) {
      administrationLinks.push({
        itemType: 'link',
        itemKey: 'workspace',
        title: this.translateService.t('pageTitle.user.settings.workspace'),
        route: workspaceRoute.to,
        icon: 'tmi-organization-domain',
        overrideActive:
          typeof currentRoute.name === 'string' &&
          (currentRoute.name.startsWith(UserSettingsRoutes.WorkspaceSubaccounts) ||
            currentRoute.name.startsWith(UserSettingsRoutes.WorkspaceUsers) ||
            currentRoute.name.startsWith(UserSettingsRoutes.WorkspaceSso) ||
            currentRoute.name.startsWith(UserSettingsRoutes.WorkspaceDataRetention))
            ? true
            : undefined,
        childrenRoutesList: this.getWorkspaceChildRoutes(),
      })
    }

    administrationLinks.push({
      itemType: 'link',
      itemKey: 'billing',
      title: this.translateService.t('pageTitle.user.settings.billing'),
      route: { name: UserSettingsRoutes.BillingIndex },
      icon: 'account_balance',
      childrenRoutesList: this.getBillingChildRoutes(),
    })

    administrationLinks.push({
      itemType: 'link',
      itemKey: 'account',
      title: this.translateService.t('pageTitle.user.settings.account'),
      route: { name: UserSettingsRoutes.AccountIndex },
      icon: 'tmi-account-circle-filled',
      childrenRoutesList: this.getAccountChildRoutes(),
    })

    const _menu: PageContentSidebarGroup[] = [
      {
        itemType: 'group',
        itemKey: 'channels',
        title: this.translateService.t('common.channels'),
        links: channelsLinks,
      },
      {
        itemType: 'group',
        itemKey: 'features',
        title: this.translateService.t('common.features'),
        links: featuresLinks,
      },
      {
        itemType: 'group',
        itemKey: 'administration',
        title: this.translateService.t('common.administration'),
        links: administrationLinks,
      },
    ]
    return _menu
  }

  public getFilteredRoutesGroups() {
    const _menu = this.getRoutesGroups()

    const menu: PageContentSidebarGroup[] = []
    _menu.forEach((t) => {
      const links = t.links.filter((item) => this.routerPermissionsService.isAllowAccessToRoute(item.route))
      if (links.length === 0) {
        return
      }
      menu.push({
        ...t,
        links,
      })
    })

    return menu
  }

  private setFirstAvailableRoute = async () => {
    const routesGroups = this.getFilteredRoutesGroups()
    const routesList = routesGroups
      .map((menuGroup) => menuGroup.links)
      .flat()
      .map((menuLink) => menuLink.route)
    const preparedRoute: TmRoutes = routesList[0] ?? {
      name: composeMessageRouteName,
    }

    await this.routerService.replace(preparedRoute)
  }

  public async resolveStrategies() {
    const routes: TmRoutes[] = []
    this.getRoutesGroups().forEach((group) => {
      group.links.forEach((link) => {
        if (link.childrenRoutesList) {
          link.childrenRoutesList.forEach((t) => routes.push(t.to))
        }
        if (link.pageRoutesList) {
          Object.values(link.pageRoutesList).forEach((t) => routes.push(t))
        }
        routes.push(link.route)
      })
    })

    await Promise.all(routes.map((t) => this.routerPermissionsService.resolveStrategiesForRoute(t)))
  }

  public async resolve() {
    await this.resolveStrategies()
    const currentRoute = this.routerService.getCurrentRoute()
    const targetRoute: TmRoutes = {
      name: UserSettingsRoutes.AccountIndex,
    }
    if (currentRoute.name !== targetRoute.name) {
      return
    }
    await this.setFirstAvailableRoute()
  }
}
